import type { Dispatch, ForwardedRef, SetStateAction } from "react";

import { Button, Flex, MultiSort, MultiSortRef, Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";

type Fields = {
  Included: string[];
  Excluded: string[];
};

type Props = {
  dataTestIdPrefix: string;
  hasFilter: boolean;
  headerMap?: { [key: string]: Optional<string> };
  onToggleAll: (target: keyof Fields) => () => void;
  onChange: Dispatch<SetStateAction<Fields>>;
  filteredItems: Fields;
  items: Fields;
  handleRef?: Nullable<ForwardedRef<MultiSortRef>>;
  onSelectionChange?: (selectedIds: string[]) => void;
  hideFieldCount?: boolean;
  disabledIds?: string[];
  disableToggleAll?: boolean;
};

export const FieldMultiSort = (props: Props) => (
  <MultiSort
    dataTestIdPrefix={props.dataTestIdPrefix}
    multiSelect
    items={props.filteredItems}
    setItems={props.onChange}
    disabledIds={props.disabledIds}
    renderContainerHeader={headerProps => {
      let headerLabel = props.headerMap?.[headerProps.id];
      if (!headerLabel) {
        headerLabel =
          headerProps.id === "Included" ? appstr("Included fields") : appstr("Excluded fields");
      }

      return (
        <Flex
          dataTestId={headerProps.dataTestId}
          className="oc-multisort-header"
          direction="column"
          gap="8px"
          padding="8px 6px"
        >
          <Flex gap="4px" justifyContent="space-between" alignContent="baseline">
            <Flex gap="4px">
              <Text modifier="medium">{headerLabel}</Text>
              {!props.hideFieldCount && (
                <FieldContainerCount
                  hasFilter={props.hasFilter}
                  filteredCount={
                    props.filteredItems[headerProps.id as "Included" | "Excluded"].length
                  }
                  unfilteredCount={props.items[headerProps.id as "Included" | "Excluded"].length}
                />
              )}
            </Flex>
            <Button
              dataTestId={`${props.dataTestIdPrefix}-${headerProps.id}-toggle-all`}
              modifier="secondary"
              size="small"
              disabled={props.disableToggleAll}
              onClick={props.onToggleAll(headerProps.id as "Included" | "Excluded")}
            >
              {headerProps.id === "Included" ? appstr("Include all") : appstr("Exclude all")}
            </Button>
          </Flex>
        </Flex>
      );
    }}
    handleRef={props.handleRef}
    onSelectionChange={props.onSelectionChange}
  />
);

type FieldContainerCountProps = {
  hasFilter: boolean;
  filteredCount: number;
  unfilteredCount: number;
};

const FieldContainerCount = (props: FieldContainerCountProps) => (
  <Text>
    (
    {props.hasFilter
      ? `${props.filteredCount} / ${props.unfilteredCount}`
      : `${props.unfilteredCount}`}
    )
  </Text>
);
